import { useQuery } from "@tanstack/react-query";
import { CarFront, KeyRound } from "lucide-react";
import { withRequestSignal } from "@/lib/api/request-signal";
import type { CommandGroup, CommandItem } from "@altek/ui/components/ui/CommandPalette";
import { formatDate, formatDateRange } from "@altek/ui/lib/format/date";
import { messageFor } from "@altek/ui/lib/problem";
import { directoryApi } from "@/features/directory/api/directory.api";
import { employeesApi } from "@/features/employees/api/employees.api";
import { eventsApi, physiciansApi } from "@/features/events/api/events.api";
import { fleetVehicleApi } from "@/features/fleet/api/fleet.api";
import { VEHICLE_STATE_LABELS } from "@/features/fleet/lib/fleet-labels";
import { vehicleQuickAction } from "@/features/fleet/lib/vehicle-quick-action";
import { inventoryApi } from "@/features/inventory/api/inventory.api";
import type { DocumentType } from "@/features/inventory/api/inventory.types";
import { DOCUMENT_TYPE_LABELS } from "@/features/inventory/lib/document-labels";
import { balancesHref, documentHref, lotsHref } from "@/features/inventory/lib/inventory-links";
import { leaveApi } from "@/features/leave/api/leave.api";
import { partnersApi } from "@/features/partners/api/partners.api";
import { presentationRequestsApi } from "@/features/presentation-requests/api/presentation-requests.api";
import { productRequestsApi } from "@/features/product-requests/api/product-requests.api";
import { salesApi } from "@/features/sales/api/sales.api";
import {
  ORDER_STATUS_LABELS,
  QUOTATION_STATUS_LABELS,
  labelOf,
} from "@/features/sales/lib/sales-labels";
import type { PermissionSet } from "@/lib/permissions/permissions";

/**
 * ÜST ARAMA KAYIT DA BULUYOR — 7 Ekim 2026, kullanılabilirlik turu.
 *
 * Arama kutusu «Ara… kişi, izin, belge, ürün» diyor (yazı kit'te sabit), ama palet yalnız menü
 * satırlarının adlarını tarıyordu: «Selin», «Parasetamol», «izin» — hepsi «Sonuç yok». Beş
 * incelemecinin dördü bunu en üst sıraya koydu. Şimdi palet üç şey buluyor:
 *
 *   1. EKRANLAR — menü satırları, artı menüde OLMAYAN kişisel ekranlar (izin taleplerim,
 *      belgelerim, eğitimlerim…). Onlar 6 Ekim'de menüden Panelim kutucuklarına taşındı; kısa
 *      menü kararı geçerli, ama aranınca bulunmaları gerekiyor.
 *   2. EYLEMLER — «Yeni izin talebi» gibi, doğrudan formu açan kısa yollar. 1 Kasım 2026'dan
 *      beri kataloğu `app/navigation/quick-actions.ts` («Hızlı işlemler»); buradaki yalnız terimden
 *      doğanlar: karekod (`codeLookupActions`) ve plakadan «İade al» / «Zimmet ver».
 *   3. KAYITLAR — kişi, personel kartı, kongre, hekim, ürün, iş ortağı; her kaynak kendi okuma
 *      anahtarıyla kapılı ve en fazla beş satır. Sunucuya yeni bir arama ucu eklenmedi: her
 *      liste ucunun zaten aldığı `q` kullanılıyor, yani arama listede görebildiğinden fazlasını
 *      göstermiyor.
 */

export interface SearchableScreen {
  href: string;
  label: string;
  /** Second line in the palette — where it lives. */
  description: string;
  /** Extra words people type for it («bakiye» for the leave ledger). */
  keywords: string;
  permission: string | null;
  module?: string;
  /**
   * Opens the person's OWN records — meaningless without an ACTIVE employment (patron, a
   * department login, a hire whose start has not been run), where the page only says «bağlı
   * değil» or «henüz başlamadı». Panelim hides the same tiles in that state.
   */
  needsEmployee?: boolean;
  /** About OTHER people's records (a department tab): gated with `canForOthers`. */
  aboutOthers?: boolean;
  /**
   * Bölüm ekranları için: rotanın kapısını MİRAS ALDIĞI menü satırı — `EXTRA_ROUTES`'taki
   * `under` ile AYNI değer olmak zorunda, ve `global-search.test.ts` ikisini karşılaştırıyor.
   * Ekranın kendi anahtarı bölümün kapısından dar ya da geniş olabilir; palet ikisini birden
   * sormazsa router'ın reddettiği bir adresi önerir (gerekçe `screenAllowed`'da).
   */
  under?: string;
  /**
   * Rotanın kendi kapısı, ekranın anahtarından FARKLIYSA — «ne yapmak istediğin» ile «hangi
   * sayfanın açıldığı» aynı anahtar olmak zorunda değil.
   *
   * «Yeni izin talebi» bunun örneği: eylem `hr.leave.request` ister (talebi açan o), ama
   * açtığı adres `/my-leave/requests` ve o sayfanın kapısı `hr.leave.read`. İkisini birden
   * sormazsak, okuma anahtarı olmayan birine açamayacağı bir eylem öneririz.
   */
  routeNeeds?: readonly string[];
}

/** Self-service screens that are reached from Panelim tiles, not from the menu. */
export const PERSONAL_SCREENS: readonly SearchableScreen[] = [
  /**
   * «İzinlerim» — izin-sade 20 Ekim 2026. Talepler, takvim ve hareketler tek ekranın üç sekmesi;
   * «Sağlık raporlarım» kalktı (rapor izin talebinin içinde). Rapor yazan kişi de «İzinlerim»i bulur.
   */
  {
    href: "/my-leave/requests",
    needsEmployee: true,
    label: "İzinlerim",
    description: "Kişisel",
    keywords: "izin talep yıllık mazeret rapor sağlık istirahat belge taleplerim",
    permission: "hr.leave.read",
    module: "module.hr",
  },
  {
    href: "/my-leave/calendar",
    needsEmployee: true,
    label: "İzin takvimim",
    description: "İzinlerim › Takvim",
    keywords: "izin takvim",
    permission: "hr.leave.read",
    module: "module.hr",
  },
  {
    href: "/my-leave/ledger",
    needsEmployee: true,
    label: "İzin hareketlerim",
    description: "İzinlerim › Hareketler",
    keywords: "izin bakiye hak ediş kalan defter",
    permission: "hr.leave.read",
    module: "module.hr",
  },
  {
    href: "/company/my-documents",
    needsEmployee: true,
    label: "Belgelerim",
    description: "Kişisel",
    keywords: "belge özlük evrak yükle dosya",
    permission: "hr.document.read",
  },
  {
    href: "/my-training",
    needsEmployee: true,
    label: "Eğitimlerim",
    description: "Kişisel",
    keywords: "eğitim kurs isg",
    permission: "hr.training.self",
    module: "module.hr",
  },
  {
    href: "/my-presentation-requests",
    needsEmployee: true,
    label: "Sunum taleplerim",
    description: "Kişisel",
    keywords: "sunum talep",
    permission: "events.presentation.request",
    module: "module.events",
  },
  {
    href: "/profile",
    label: "Profilim",
    description: "Kişisel",
    keywords: "profil fotoğraf telefon ad",
    // `null` DEĞİL: rotanın kapısı bu anahtar (`EXTRA_ROUTES` › `gate`). Kapısız bırakıldığında
    // anahtarı olmayan hesap «Profilim»i aramada görüp «Sayfa bulunamadı» alırdı. Bugün her
    // EMPLOYEE bunu taşıyor, yani kimse etkilenmiyordu — ama eşitlik tesadüftü, kural değildi.
    permission: "directory.profile.manage",
  },
];

/**
 * Section TABS worth finding by name (7 Ekim 2026, ikinci inceleme turu): «belge» yazan İK
 * müdürü kendi «Personel belgeleri» sekmesini bulamıyordu — menü bölüme işaret ediyor, sekme
 * menü satırı değil. Her satır sekmenin KENDİ okuma anahtarıyla ve modülüyle kapılı
 * (`LeaveSectionPage`, `InventorySectionPage`, `ChannelSectionPage`, `AuditSectionPage`,
 * Personel bölümü); anahtarı olmayan kişi sekmeyi aramada da görmüyor.
 *
 * İkinci satır rayın yolu: «İK › Personel», «Satış › Kanal» — stok sekmeleri de «Depo › Stok»
 * (9 Ekim 2026; yalnız «Stok» yazıyordu, rayda grup «Depo», satır «Stok»).
 */
export const SECTION_SCREENS: readonly SearchableScreen[] = [
  {
    href: "/hr/people/documents",
    under: "/hr/people",
    label: "Personel belgeleri",
    description: "İK › Personel",
    keywords: "belge özlük evrak inceleme gelen süresi yaklaşan",
    permission: "hr.document.read",
    module: "module.hr",
    aboutOthers: true,
  },
  {
    // Girişler sekmesi kalktı (21 Ekim 2026): yeni girenler ve başlamayı bekleyenler Çalışanlar'da.
    href: "/hr/people/employees",
    under: "/hr/people",
    label: "İşe girişler",
    description: "İK › Personel › Çalışanlar",
    keywords: "giriş başlangıç yeni personel istihdam",
    permission: "hr.employment.read",
    module: "module.hr",
    aboutOthers: true,
  },
  {
    href: "/hr/people/exits",
    under: "/hr/people",
    label: "İşten çıkışlar",
    description: "İK › Personel",
    keywords: "çıkış ayrılış istifa fesih",
    permission: "hr.employment.read",
    module: "module.hr",
    aboutOthers: true,
  },
  {
    href: "/hr/leave/balances",
    under: "/hr/leave",
    label: "İzin bakiyeleri",
    description: "İK › İzin",
    keywords: "izin bakiye tablo hak ediş kalan",
    permission: "hr.leave.read",
    module: "module.hr",
    aboutOthers: true,
  },
  {
    href: "/hr/leave/policies",
    under: "/hr/leave",
    label: "İzin politikası ve türleri",
    description: "İK › İzin",
    keywords: "politika kıdem izin türü",
    permission: "hr.leave.read",
    module: "module.hr",
    aboutOthers: true,
  },
  // ik-yasam-dongusu 19 Ekim 2026 (İK-16): resmî tatil yönetimi.
  {
    href: "/hr/leave/holidays",
    under: "/hr/leave",
    label: "Resmî tatiller",
    description: "İK › İzin",
    keywords: "tatil bayram arife resmi tatil takvim yarım gün",
    permission: "hr.leave.read",
    module: "module.hr",
    aboutOthers: true,
  },
  {
    href: "/hr/leave/reports",
    under: "/hr/leave",
    label: "Sağlık raporları",
    description: "İK › İzin",
    keywords: "rapor istirahat sağlık",
    permission: "hr.medical_report.read",
    module: "module.hr",
    aboutOthers: true,
  },
  {
    href: "/inventory/products",
    under: "/inventory",
    label: "Ürünler ve partiler",
    description: "Depo › Stok",
    keywords: "ürün parti lot miad skt seri",
    permission: "inventory.product.read",
    module: "module.inventory",
    aboutOthers: true,
  },
  {
    href: "/inventory/availability",
    under: "/inventory",
    label: "Kullanılabilirlik",
    description: "Depo › Stok",
    keywords: "kullanılabilir söz verilebilir rezerve rezervasyon miktar",
    permission: "inventory.balance.read",
    module: "module.inventory",
    aboutOthers: true,
  },
  {
    href: "/inventory/movements",
    under: "/inventory",
    label: "Depo işlemleri",
    description: "Depo › Stok",
    keywords: "belge mal kabul sevk sayım its",
    permission: "inventory.document.read",
    module: "module.inventory",
    aboutOthers: true,
  },
  /**
   * Kanal sekmeleri (ticari veri, 31 Ekim 2026) — her biri sekmenin KENDİ okuma anahtarıyla
   * (`ChannelSectionPage` › TABS); eski «Kanal verisi» (`/sales/channel/facts`) özete yönleniyor.
   */
  {
    href: "/sales/channel/summary",
    under: "/sales/channel",
    label: "Kanal özeti",
    description: "Satış › Kanal",
    keywords: "özet ticari depo çıkışı ims mal fazlası mf",
    permission: "channel.analysis.read",
    module: "pharma.ims",
    aboutOthers: true,
  },
  {
    href: "/sales/channel/analysis",
    under: "/sales/channel",
    label: "Satış analizi",
    description: "Satış › Kanal",
    keywords: "kim ne kadar sattı satış müdürü bölge müdürü mümessil brick il ürün ecza deposu ims",
    permission: "channel.analysis.read",
    module: "pharma.ims",
    aboutOthers: true,
  },
  {
    href: "/sales/channel/reconciliation",
    under: "/sales/channel",
    label: "Kanal mutabakatı",
    description: "Satış › Kanal",
    keywords: "mutabakat fark depo ims açık kaçak olağan dışı iade veri tamlığı logo erp",
    permission: "channel.reconciliation.read",
    module: "pharma.ims",
    aboutOthers: true,
  },
  {
    href: "/sales/channel/imports",
    under: "/sales/channel",
    label: "Kanal yüklemeleri",
    description: "Satış › Kanal",
    keywords: "yükleme depo raporu dosya ecza ims iqvia logo posta",
    permission: "channel.report.read",
    module: "pharma.ims",
    aboutOthers: true,
  },
  {
    href: "/sales/channel/rules",
    under: "/sales/channel",
    label: "Kanal kuralları",
    description: "Satış › Kanal",
    keywords: "kural eşleme tanınmayan ürün şube jeton ecza deposu saha yapısı brick",
    permission: "channel.mapping.read",
    module: "pharma.ims",
    aboutOthers: true,
  },
  {
    href: "/sales/channel/pharmacy-pool",
    under: "/sales/channel",
    label: "Eczane havuzu",
    description: "Satış › Kanal",
    keywords: "eczane havuz gln brick",
    permission: "channel.mapping.manage",
    module: "pharma.ims",
    aboutOthers: true,
  },
  {
    href: "/admin/audit/integrity",
    under: "/admin/audit",
    label: "Bütünlük bulguları",
    description: "Yönetim › Denetim",
    keywords: "bütünlük kritik bulgu denetim zincir",
    permission: "audit.integrity.read",
    aboutOthers: true,
  },
];

/**
 * Bir ekranın ya da işlemin KAPISI — `screenAllowed`'ın sorduğu alanlar. «Hızlı işlemler»
 * (`quick-actions.ts`) de aynı kapıdan geçiyor; adresi olmayan bir komut da.
 */
export type ScreenGate = Pick<
  SearchableScreen,
  "needsEmployee" | "under" | "routeNeeds" | "permission" | "aboutOthers" | "module"
>;

/**
 * BİR BÖLÜM EKRANI, BÖLÜMÜN KAPISINDAN DA GEÇMEK ZORUNDA — 24 Ekim 2026, kullanıcı bulgusu.
 *
 * Buradaki `permission` ekranın KENDİ anahtarı; rotanın kapısı ise `EXTRA_ROUTES`'taki `under`
 * ile bölümün menü satırından MİRAS alınıyor. İkisi aynı sanılıyordu ve değildi:
 * `/inventory/products` bu listede `inventory.product.read` ile duruyor, ama bölümün satırı
 * (`Stok`, `/inventory`) bilerek `inventory.balance.read`'e bağlandı — gerekçesi
 * `nav-catalog.ts`'te yazılı: «ürün okumasını bölümün kapısı olmaktan çıkarıyor».
 *
 * Sonuç ölçüldü: ürün anahtarı olan ama depoya girmeyen bir tıbbi mümessil, paletin
 * «Ürünler ve partiler · Depo › Stok» satırını görüyor, tıklayınca «Sayfa bulunamadı» alıyordu.
 * Yani palet, 404 ekranının GİZLEDİĞİ sayfanın adını söylüyordu — bu dosyanın başındaki kuralın
 * tam tersi. Sunucu veriyi vermiyor, ama hangi ekranların var olduğunu söylemek de bir sızıntı.
 *
 * KAPALI TARAFA DÜŞÜYOR: `under` taşıyan bir ekran, görünür bölüm kümesi VERİLMEDİĞİNDE de
 * elenir. Çağıranın unutması, sızıntının geri gelmesi demek olurdu; unutulduğunda ekran
 * kaybolur — fark edilir ve düzeltilir, sessizce açılmaz.
 */
export function screenAllowed(
  screen: ScreenGate,
  permissions: PermissionSet,
  hasEmployee = true,
  visibleSections?: ReadonlySet<string>,
): boolean {
  if (screen.needsEmployee === true && !hasEmployee) return false;
  if (screen.under !== undefined && !(visibleSections?.has(screen.under) ?? false)) return false;
  // Rotanın kendi kapısı: `ProtectedRoute` bunları `all` olarak soruyor, yani hepsi gerekli ve
  // kayıt kapsamı aranmıyor (`gate` rotalarında `aboutOthers` sabit false).
  if (screen.routeNeeds?.some((key) => !permissions.can(key)) === true) return false;
  if (screen.permission !== null) {
    const holds = screen.aboutOthers === true ? permissions.canForOthers : permissions.can;
    if (!holds(screen.permission)) return false;
  }
  return screen.module === undefined || permissions.isModuleEnabled(screen.module);
}

/**
 * Sekme başlığının kişisel ekran adları — İSTİHDAM ŞARTI OLMADAN (9 Ekim 2026, 3. tur, İK
 * ajanının bulgusu). Aramada ve Panelim'de bu ekranlar istihdamı olmayana önerilmiyor
 * (`needsEmployee`); ama rotalarının kapısı yalnız anahtar, yani adresi yazan ya da bir
 * bağlantıyla gelen kişi ekrana ulaşıyor ve ekran «bağlı değil» diyor. Başlık o durumda «ALTEK
 * ERP» kalıyordu. Ekrana gelinebildiği sürece başlık da geliyor.
 */
export function personalScreenTitles(permissions: PermissionSet): SearchableScreen[] {
  return PERSONAL_SCREENS.filter((screen) => screenAllowed(screen, permissions));
}

const FOLD: Readonly<Record<string, string>> = { ç: "c", ğ: "g", ı: "i", ö: "o", ş: "s", ü: "u" };

/**
 * Lower-cased the Turkish way, then with the Turkish letters folded to their plain twins: a
 * person types «ISG», «sagl», «sahin» as often as «İSG», «sağl», «Şahin». The Turkish lower-case
 * of «I» is «ı», so without the fold «ISG» would never find «İSG».
 */
function fold(text: string): string {
  return text.toLocaleLowerCase("tr").replace(/[çğıöşü]/g, (letter) => FOLD[letter] ?? letter);
}

/** Turkish-aware «contains», over the label, the description and the keywords. */
export function matchesTerm(term: string, ...texts: Array<string | null | undefined>): boolean {
  const needle = fold(term.trim());
  if (!needle) return true;
  return texts.some((text) => fold(text ?? "").includes(needle));
}

/** Records are searched from this many characters on — one letter matches half the company. */
export const RECORD_SEARCH_MIN = 2;
const PER_SOURCE = 5;

/** Which record sources this person may search — each one gated by its own read key. */
export interface RecordSearchSources {
  people: boolean;
  employees: boolean;
  events: boolean;
  physicians: boolean;
  products: boolean;
  partners: boolean;
  /**
   * NUMARAYLA ARANANLAR (9 Ekim 2026, `ERP_UX_KARSILASTIRMA.md` AR-1). Yalnız terim bir numaraya
   * benzediğinde soruluyor (`looksLikeNumber`) — adla aramada gürültü ve boşa istek olmasın.
   * Her biri kendi listesinin okuma anahtarıyla ve modülüyle kapılı; listede göremediğini aramada
   * da görmüyor. İzin talebi yalnız BAŞKALARININ talebini okuyana (İK): telefonla sorulan
   * «IZN-2026-0054» onun sorusu; çalışanın kendi talepleri «İzin taleplerim»de.
   */
  leaveRequests: boolean;
  stockDocuments: boolean;
  lots: boolean;
  presentations: boolean;
  productRequests: boolean;
  /**
   * SATIŞ BELGELERİ NUMARAYLA (satis-duzeltme 20 Ekim 2026, 19 Ekim denetimi S-24): «SIP-2026-0003»,
   * «TKL-2026-0002» «Sonuç yok» diyordu — satışın en sık sorusu «şu sipariş nerede?». Teklif ve
   * sipariş kendi okuma anahtarıyla; sipariş okuyamayan DEPO sevk bekleyen siparişi buluyor
   * (`inventory.shipment.manage`, fiyatsız görünüm).
   */
  quotations: boolean;
  orders: boolean;
  shipments: boolean;
  /** Karekod Sorgula ekranı: karekodu (GS1) olduğu gibi oraya götüren eylem. */
  codeLookup: boolean;
  /**
   * FİLO ARAÇLARI — PLAKAYLA (20 Ekim 2026, filo denetimi F-20). «34 ABC 123» yazan filo müdürü
   * aracın kartına gitsin; marka ve modelle de bulunur. Araç listesinin okuma anahtarı ve filo
   * modülüyle kapılı.
   */
  vehicles: boolean;
  /**
   * PLAKADAN İŞLEM (1 Kasım 2026, «Hızlı işlemler»): zimmet yetkisi olan, bulunan aracın tek işini
   * de görüyor — zimmetliyse «İade al», müsaitse «Zimmet ver» (`vehicleQuickAction`, araç kartıyla
   * aynı kural). «34 ABC» yazıp Enter'a basmak iade sihirbazını açıyor.
   */
  vehicleActions: boolean;
  /**
   * ÜRÜN SONUCU NEREYE AÇILIYOR (9 Ekim 2026, 3. tur — depo ajanının önerisi). Bakiye okuyabilen
   * kişinin sorusu «nerede, ne kadar»: Stok Durumu, ürünün koduyla süzülmüş (`balancesHref`).
   * Okuyamayana ürün kartları — kataloğun kendisi.
   */
  productStock: boolean;
}

export function recordSearchSources(permissions: PermissionSet): RecordSearchSources {
  const inventoryOn = permissions.isModuleEnabled("module.inventory");
  const eventsOn = permissions.isModuleEnabled("module.events");
  const salesOn = permissions.isModuleEnabled("module.sales");
  // Karekod Sorgula İTS modülünün (1 Kasım 2026): kapalı kurulumda karekod eylemi yok, parti Ürünler'de açılıyor.
  const codeLookup = permissions.canForOthers("inventory.balance.read") && inventoryOn && permissions.isModuleEnabled("pharma.its");
  const orders = permissions.can("sales.order.read") && salesOn;
  const vehicles = permissions.can("fleet.vehicle.read") && permissions.isModuleEnabled("module.fleet");
  return {
    quotations: permissions.can("sales.quotation.read") && salesOn,
    orders,
    // Siparişi zaten okuyabilen satışa ikinci kez gösterilmez. Sevk bekleyen siparişler Satış'ın uçları.
    shipments: !orders && permissions.can("inventory.shipment.manage") && inventoryOn && salesOn,
    people: permissions.can("directory.person.read"),
    employees:
      permissions.canForOthers("hr.employee.read") && permissions.isModuleEnabled("module.hr"),
    events: permissions.can("events.event.read") && eventsOn,
    physicians: permissions.can("events.participant.read") && eventsOn,
    products: permissions.can("inventory.product.read") && inventoryOn,
    partners: permissions.can("partners.partner.read"),
    leaveRequests:
      permissions.canForOthers("hr.leave.read") && permissions.isModuleEnabled("module.hr"),
    stockDocuments: permissions.canForOthers("inventory.document.read") && inventoryOn,
    // Parti satırı Karekod Sorgula'ya açılıyor: o ekranın kapısı (stok okuma) da gerekiyor. İTS kapalıysa
    // Ürünler › Partiler'e açılıyor — o sekmenin kapısı ürün okuma.
    lots:
      permissions.canForOthers("inventory.lot.read") &&
      (codeLookup || permissions.can("inventory.product.read")) &&
      inventoryOn,
    presentations: permissions.canForOthers("events.presentation.read") && eventsOn,
    productRequests:
      permissions.canForOthers("marketing.product_request.read") &&
      permissions.isModuleEnabled("module.crm"),
    codeLookup,
    productStock: permissions.canForOthers("inventory.balance.read") && inventoryOn,
    vehicles,
    vehicleActions: vehicles && permissions.can("fleet.assignment.manage"),
  };
}

/**
 * Plakaya benzeyen terim plakanın SAKLANAN biçimine çevrilir (`normalisePlate`, sunucu): «34 abc 123»,
 * «34-ABC-123» → «34ABC123». Liste araması `contains` ile yapıldığından boşluklu yazım hiçbir plakayı
 * bulmuyordu. İl kodu + harf ile başlamayan terim (marka, model) olduğu gibi kalır.
 */
const PLATE_FOLD: Readonly<Record<string, string>> = { İ: "I", Ş: "S", Ğ: "G", Ü: "U", Ö: "O", Ç: "C" };
export function plateSearchTerm(term: string): string {
  const q = term.trim();
  if (!/^\d{2}[\s._-]*[a-zA-ZçğıöşüÇĞİÖŞÜ]{1,3}(?:[\s._-]*\d{0,5})$/.test(q)) return q;
  return q
    .replace(/[\s._-]+/g, "")
    .toLocaleUpperCase("tr-TR")
    .replace(/[İŞĞÜÖÇ]/g, (char) => PLATE_FOLD[char] ?? char);
}

/** Numara arayabiliyor mu — yer tutucunun «numara» sözü bu. */
function searchesNumbers(sources: RecordSearchSources): boolean {
  return (
    sources.leaveRequests ||
    sources.stockDocuments ||
    sources.lots ||
    sources.presentations ||
    sources.productRequests ||
    sources.quotations ||
    sources.orders ||
    sources.shipments ||
    sources.codeLookup
  );
}

/**
 * Bir numara ya da kod gibi mi: en az üç işaret, en az bir rakam, boşluk yok — «IZN-2026-0054»,
 * «ISS-2026-0001», «LDEMO2003», «SNT-2026-0001». Ad aramasında numara kaynakları sorulmuyor.
 */
export function looksLikeNumber(term: string): boolean {
  const q = term.trim();
  return q.length >= 3 && /\d/.test(q) && !/\s/.test(q);
}

/**
 * Bir GS1 karekodu mu — ilaç kutusundaki DataMatrix'in metni: «01» + 14 haneli GTIN ile başlıyor;
 * parantezli yazım («(01)…») ve okuyucunun öneki («]d2») de. Bu metin hiçbir listenin `q`'suyla
 * eşleşmez: doğru yer Karekod Sorgula, ve oraya olduğu gibi gidiyor.
 */
export function looksLikeGs1(term: string): boolean {
  const q = term.trim().replace(/^\][A-Za-z]\d/, "");
  return /^(\(01\)|01)\d{14}/.test(q);
}

/**
 * Karekodu Karekod Sorgula'ya götüren eylem — sorgu atmadan, terimin kendisinden. Ekran kodu
 * okuyup ürünü, partiyi ve seriyi buluyor; bulamazsa partisini ve serisini ayrıca önerebiliyor.
 */
export function codeLookupActions(term: string, sources: RecordSearchSources): CommandItem[] {
  const code = term.trim();
  if (!sources.codeLookup || !looksLikeGs1(code)) return [];
  return [
    {
      id: `eylem:/inventory/lookup?code=${encodeURIComponent(code)}`,
      label: "Karekodu sorgula",
      description: "Karekod Sorgula — ürün, parti, seri ve stok",
    },
  ];
}

/**
 * ARAMA KUTUSU, BU KİŞİNİN ARAYABİLDİĞİ ŞEYİ SÖYLER (8 Ekim 2026, kullanılabilirlik turu).
 *
 * Üst çubuk «Ara… kişi, izin, belge, ürün», palet «Ekran, kişi, kongre, ürün ara…» diyordu: iki
 * kutu iki farklı söz veriyordu ve ikisi de herkese aynıydı. Bir çalışanın paletinde kongre ya da
 * ürün aranmıyor (anahtarı yok), izin ve belge KAYDI hiç kimse için aranmıyor — «izin» yazan kişi
 * izin ekranlarını buluyor, izin taleplerini değil. Artık metin, aynı kapılardan
 * (`recordSearchSources`) kuruluyor: ekranlar herkese, kayıt türleri yalnız okuyabilene.
 *
 * Üst çubuktaki tetiğin yazısı kitte sabit (`AppNavbar`'ın bir `searchPlaceholder` prop'u yok);
 * aynı metni oraya vermek kit isteği olarak raporlandı.
 */
export function searchPlaceholderFor(sources: RecordSearchSources): string {
  const words = ["Ekran"];
  if (sources.people || sources.employees) words.push("kişi");
  if (sources.events) words.push("kongre");
  if (sources.physicians) words.push("hekim");
  if (sources.products) words.push("ürün");
  if (sources.partners) words.push("iş ortağı");
  if (sources.vehicles) words.push("plaka");
  // İzin no, belge no, parti, karekod, talep no — hepsi tek sözcükle (9 Ekim 2026).
  if (searchesNumbers(sources)) words.push("numara");
  const last = words.pop() ?? "Ekran";
  return words.length === 0 ? `${last} ara…` : `${words.join(", ")} ya da ${last} ara…`;
}

interface SourceResult {
  id: string;
  label: string;
  items: CommandItem[];
  /** Bulunan kaydın kendi işi — «Hızlı işlemler»in başına çıkıyor (plakadan «İade al»). */
  actions?: CommandItem[];
}

function compact(items: Array<CommandItem | null>): CommandItem[] {
  return items.filter((item): item is CommandItem => item !== null);
}

/**
 * The record half of the palette. One query for all sources, so the palette has ONE loading
 * state and ONE error; a source that fails is dropped rather than failing the others.
 */
export function useRecordSearch(input: {
  term: string;
  permissions: PermissionSet;
  legalEntityId: string | null;
  enabled: boolean;
}) {
  const { term, permissions, legalEntityId, enabled } = input;
  const q = term.trim();
  const can = recordSearchSources(permissions);
  /**
   * Numara kaynakları yalnız numaraya benzeyen terimde; karekod hiçbir listede aranmıyor — onu
   * `codeLookupActions` Karekod Sorgula'ya götürüyor, burada boşa istek atılmıyor.
   */
  const numberLike = looksLikeNumber(q);
  const gs1 = looksLikeGs1(q);
  const nameSources =
    can.people ||
    can.employees ||
    can.events ||
    can.physicians ||
    can.products ||
    can.partners ||
    can.vehicles;
  const numberSources =
    numberLike &&
    (can.leaveRequests ||
      can.stockDocuments ||
      can.lots ||
      can.presentations ||
      can.productRequests ||
      can.quotations ||
      can.orders ||
      can.shipments);
  const anySource = !gs1 && (nameSources || numberSources);

  const active = enabled && anySource && q.length >= RECORD_SEARCH_MIN;

  const query = useQuery({
    queryKey: ["global-search", q, legalEntityId, can] as const,
    enabled: active,
    staleTime: 30_000,
    queryFn: async ({ signal }): Promise<SourceResult[]> => {
      /*
        ÖNCEKİ TERİMİN İSTEKLERİ İPTAL (31 Ekim 2026, UI/UX denetimi K-A20): terim değişince bu
        sorgunun gözlemcisi kalmıyor ve TanStack sinyali kesiyor. Kaynakların hiçbiri sinyal
        parametresi almıyor; istekler bu bloğun içinde başladığı için sinyali istemciden alıyor.
      */
      const sources = withRequestSignal(signal, () => {
        const started: Array<Promise<SourceResult | null>> = [];

        if (can.people) {
          started.push(
            directoryApi.people({ q, page: 1, limit: PER_SOURCE }).then((page) => ({
              id: "kisiler",
              label: "Kişiler",
              items: page.rows.map((person) => ({
                id: `kayit:/directory?record=${encodeURIComponent(person.userId)}`,
                label: person.displayName,
                description:
                  [person.jobTitle, person.orgUnit?.name].filter(Boolean).join(" · ") ||
                  "Kişi Rehberi",
              })),
            })),
          );
        }

        if (can.employees) {
          started.push(
            employeesApi.list({ q, page: 1, limit: PER_SOURCE }).then((page) => ({
              id: "personel",
              label: "Personel kartları",
              items: page.rows.map((employee) => ({
                id: `kayit:/hr/employees/${encodeURIComponent(employee.id)}`,
                label: employee.fullName,
                description: `Personel no ${employee.publicNo}`,
              })),
            })),
          );
        }

        if (can.events) {
          started.push(
            eventsApi
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "kongreler",
                label: "Kongre ve etkinlikler",
                items: page.rows.map((event) => ({
                  id: `kayit:/events/${encodeURIComponent(event.id)}`,
                  label: event.name,
                  description: event.code,
                })),
              })),
          );
        }

        if (can.physicians) {
          started.push(
            physiciansApi.list({ q, page: 1, limit: PER_SOURCE }).then((page) => ({
              id: "hekimler",
              label: "Hekimler",
              items: page.rows.map((physician) => ({
                id: `kayit:/physicians?q=${encodeURIComponent(physician.fullName)}`,
                label: physician.fullName,
                description: "Hekim",
              })),
            })),
          );
        }

        if (can.products) {
          started.push(
            inventoryApi.products.list({ q, page: 1, limit: PER_SOURCE }).then((page) => ({
              id: "urunler",
              label: "Ürünler",
              items: page.rows.map((product) => ({
                id: can.productStock
                  ? `kayit:${balancesHref(product.code)}`
                  : `kayit:/inventory/products?products.q=${encodeURIComponent(product.code)}`,
                label: product.name,
                // İkinci satır nereye gidildiğini de söylüyor: stok mu, ürün kartı mı.
                description: can.productStock ? `${product.code} · Stok durumu` : product.code,
              })),
            })),
          );
        }

        if (can.partners) {
          started.push(
            partnersApi.list({ q, page: 1, limit: PER_SOURCE }).then((page) => ({
              id: "is-ortaklari",
              label: "İş ortakları",
              items: page.rows.map((partner) => ({
                id: `kayit:/company/partners?record=${encodeURIComponent(partner.id)}`,
                label: partner.tradeName ?? partner.legalName,
                description: partner.tradeName ? partner.legalName : "İş ortağı",
              })),
            })),
          );
        }

        if (can.vehicles) {
          started.push(
            fleetVehicleApi
              .vehicles({ q: plateSearchTerm(q), page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "araclar",
                label: "Araçlar",
                items: page.rows.map((vehicle) => ({
                  id: `kayit:/fleet/vehicles/${encodeURIComponent(vehicle.id)}`,
                  label: vehicle.plateDisplay,
                  description: [vehicle.brand, vehicle.model, vehicle.holder?.name].filter(Boolean).join(" · ") || "Filo aracı",
                })),
                actions: compact(
                  page.rows.map((vehicle) => {
                    const action = vehicleQuickAction(vehicle, can.vehicleActions);
                    if (action === null) return null;
                    const model = [vehicle.brand, vehicle.model].filter(Boolean).join(" ");
                    return {
                      id: `eylem:${action.to}`,
                      label: `${action.label} · ${vehicle.plateDisplay}`,
                      description: vehicle.holder
                        ? [vehicle.holder.name, model].filter(Boolean).join(" · ")
                        : [VEHICLE_STATE_LABELS[vehicle.state] ?? "Müsait", model].filter(Boolean).join(" · "),
                      icon: vehicle.holder ? CarFront : KeyRound,
                    };
                  }),
                ),
              })),
          );
        }

        if (numberLike && can.leaveRequests) {
          started.push(
            leaveApi
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "izin-talepleri",
                label: "İzin talepleri",
                items: page.rows.map((request) => ({
                  id: `kayit:/hr/leave/requests?record=${encodeURIComponent(request.id)}`,
                  label: request.employee
                    ? `${request.requestNo} · ${request.employee.fullName}`
                    : request.requestNo,
                  description: [
                    request.leaveType?.name,
                    formatDateRange(request.startDate, request.endDate),
                  ]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }

        if (numberLike && can.stockDocuments) {
          started.push(
            inventoryApi.documents
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "stok-belgeleri",
                label: "Stok belgeleri",
                items: page.rows.map((document) => ({
                  id: `kayit:${documentHref(document.id)}`,
                  label: document.documentNo,
                  description: [
                    DOCUMENT_TYPE_LABELS[document.documentType as DocumentType] ??
                      document.documentType,
                    document.warehouse?.name,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }

        if (numberLike && can.lots) {
          started.push(
            inventoryApi.lots
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "partiler",
                label: "Partiler",
                /**
                 * Parti, Karekod Sorgula'nın parti kipinde açılıyor: stok, depo ve hareketleri orada. İTS kapalı
                 * kurulumda o ekran yok: Ürünler › Partiler, partinin ürününe süzülmüş.
                 */
                items: page.rows.map((lot) => ({
                  id: can.codeLookup
                    ? `kayit:/inventory/lookup?code=${encodeURIComponent(lot.lotNumber)}&mode=LOT`
                    : `kayit:${lotsHref({ productId: lot.productId })}`,
                  label: `Parti ${lot.lotNumber}`,
                  description: [
                    lot.product?.name,
                    lot.expiresAt ? `SKT ${formatDate(lot.expiresAt)}` : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }

        if (numberLike && can.presentations) {
          started.push(
            presentationRequestsApi
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "sunum-talepleri",
                label: "Sunum talepleri",
                items: page.rows.map((request) => ({
                  id: `kayit:/presentation-requests?record=${encodeURIComponent(request.id)}`,
                  label: `${request.requestNo} · ${request.topic}`,
                  description: [request.requester?.name, request.targetInstitution]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }

        if (numberLike && can.productRequests) {
          started.push(
            productRequestsApi
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "urun-talepleri",
                label: "Ürün talepleri",
                items: page.rows.map((request) => ({
                  id: `kayit:/product-requests/${encodeURIComponent(request.id)}`,
                  label: `${request.requestNo} · ${request.name}`,
                  description: "Ürün talebi",
                })),
              })),
          );
        }

        if (numberLike && can.quotations) {
          started.push(
            salesApi.quotations
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "teklifler",
                label: "Teklifler",
                items: page.rows.map((quotation) => ({
                  id: `kayit:/sales/quotations?panel=edit&record=${encodeURIComponent(quotation.id)}`,
                  label: quotation.partnerName
                    ? `${quotation.documentNo} · ${quotation.partnerName}`
                    : quotation.documentNo,
                  description: [
                    labelOf(QUOTATION_STATUS_LABELS, quotation.status),
                    quotation.quotationDate ? formatDate(quotation.quotationDate) : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }

        if (numberLike && can.orders) {
          started.push(
            salesApi.orders
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "siparisler",
                label: "Siparişler",
                items: page.rows.map((order) => ({
                  id: `kayit:/sales/orders?panel=edit&record=${encodeURIComponent(order.id)}`,
                  label: order.partnerName ? `${order.documentNo} · ${order.partnerName}` : order.documentNo,
                  description: [
                    labelOf(ORDER_STATUS_LABELS, order.status),
                    order.orderDate ? formatDate(order.orderDate) : null,
                  ]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }

        if (numberLike && can.shipments) {
          started.push(
            salesApi.fulfilment
              .list({ q, page: 1, limit: PER_SOURCE, ...(legalEntityId ? { legalEntityId } : {}) })
              .then((page) => ({
                id: "sevk-bekleyen",
                label: "Sevk bekleyen siparişler",
                items: page.rows.map((order) => ({
                  id: `kayit:/inventory/movements?shipments.panel=edit&shipments.record=${encodeURIComponent(order.id)}`,
                  label: order.partnerName ? `${order.documentNo} · ${order.partnerName}` : order.documentNo,
                  description: [labelOf(ORDER_STATUS_LABELS, order.status), order.warehouseName]
                    .filter(Boolean)
                    .join(" · "),
                })),
              })),
          );
        }
        return started;
      });

      const settled = await Promise.allSettled(sources);
      const results = settled
        .map((outcome) => (outcome.status === "fulfilled" ? outcome.value : null))
        .filter((result): result is SourceResult => result !== null);
      // Every source failed: say so instead of «Sonuç yok».
      if (results.length === 0 && settled.length > 0) {
        const first = settled.find((outcome) => outcome.status === "rejected");
        throw first && first.status === "rejected" ? first.reason : new Error("Arama yapılamadı");
      }
      return results;
    },
  });

  /**
   * YALNIZ BU TERİMİN SONUCU (9 Ekim 2026). Uygulamanın varsayılanı `placeholderData: previous`
   * — tablo sayfa değiştirirken eski satırları tutsun diye. Burada o, «Selin» aramasının kişi
   * kartını kutu boşaltıldıktan, tek harfe inildikten, bir karekod yazıldıktan ya da palet yeniden
   * açıldıktan SONRA da gösteriyordu: başka bir sorunun cevabı, yeni sorunun altında. Sorgu kapalıysa
   * ya da gösterilen veri önceki terimin yer tutucusuysa sonuç yok; yeni sonuç gelene kadar
   * «aranıyor».
   */
  const current = active && !query.isPlaceholderData ? query.data : undefined;

  const groups: CommandGroup[] = (current ?? [])
    .map((source) => ({ id: source.id, label: source.label, items: compact(source.items) }))
    .filter((group) => group.items.length > 0);
  const actions: CommandItem[] = (current ?? []).flatMap((source) => source.actions ?? []);

  return {
    groups,
    actions,
    isLoading: active && query.isFetching && current === undefined,
    error: active && query.error ? messageFor(query.error) : null,
  };
}
